/**
 * SuggestedPrompts — empty-state prompt chip grid (Plan 05-07b Task 2).
 *
 * Shown only when the thread has 0 messages. Renders 4 role-keyed prompt
 * chips from i18n keys `assistant.suggested.{role}.{1..4}`. Tapping a chip
 * directly submits the prompt (no intermediate input population per
 * UI-SPEC §Interaction Model: "Suggested prompts → send shortcut").
 *
 * data-testid: assistant-suggested-prompts, assistant-prompt-chip-{index}
 */
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/primitives';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import { suggestedPromptKeys, type PromptRole } from './prompts';

interface SuggestedPromptsProps {
  /** 'parent' maps to admin role for i18n key lookup */
  role: PromptRole;
  onSelect: (text: string) => void;
}

/*
 * #158 (steg 8a): den tomme tråden er ikke tegnet i skissen, så den restyles
 * med tokenene: stjernen fra toppen, overskriften i ink, og forslagene som
 * glassrader med 44 px treffflate. Oppførselen er den samme.
 */
export function SuggestedPrompts({ role, onSelect }: SuggestedPromptsProps) {
  const { t } = useTranslation();
  const prompts = suggestedPromptKeys(role).map((key) => ({ id: key, text: t(key) }));

  return (
    <div
      data-testid="assistant-suggested-prompts"
      className="flex flex-1 flex-col items-start gap-2 overflow-y-auto py-6"
    >
      <span
        aria-hidden
        className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-fab text-on-fab"
      >
        <Icon.AssistantMark size={24} />
      </span>
      <p className="text-heading-m font-extrabold text-ink">
        {t('assistant.suggested.title')}
      </p>
      <p className="text-sm text-ink-2">
        {t('assistant.empty.hint')}
      </p>
      <div className="mt-4 grid w-full grid-cols-1 gap-2">
        {prompts.map((p, idx) => (
          <button
            key={p.id}
            type="button"
            data-testid={`assistant-prompt-chip-${idx}`}
            onClick={() => onSelect(p.text)}
            className={`${GLASS_SURFACE} min-h-13 rounded-row px-4 py-3 text-left text-base font-bold text-ink hover:bg-inset`}
          >
            {p.text}
          </button>
        ))}
      </div>
    </div>
  );
}
